import { createResizeObserverWithRAF } from '@heidi/utils';
import type { RefObject } from 'react';
import { useCallback, useEffect } from 'react';

import type { ChatItem } from '../../../contracts/harness.types';
import { readToolResultText } from '../../../contracts/toolResult';

export const scrollSignalForItem = (item: ChatItem): string => {
  switch (item.kind) {
    case 'user_message':
      return `${item.id}:user:${item.text?.length ?? 0}:${item.attachments?.length ?? 0}`;
    case 'assistant_text':
      return `${item.id}:assistant:${item.streaming ? 1 : 0}:${item.text?.length ?? 0}`;
    case 'thinking':
      return `${item.id}:thinking:${item.text?.length ?? 0}`;
    case 'tool_use':
      return [
        item.id,
        'tool',
        item.status,
        item.live_output?.length ?? 0,
        readToolResultText(item)?.length ?? 0,
      ].join(':');
    case 'prompt_context_failed':
      return `${item.id}:prompt-context-failed:${item.message?.length ?? 0}`;
    case 'turn_cancelled_marker':
      return `${item.id}:turn-cancelled`;
    case 'turn_failed':
      return `${item.id}:turn-failed:${item.reason ?? ''}`;
  }
};

interface UseFollowTailArgs {
  scrollRef: RefObject<HTMLDivElement>;
  scrollContentRef: RefObject<HTMLDivElement>;
  followTailRef: RefObject<HTMLDivElement>;
  /** Seeded-start fallback target — see the "not dead code" comment on its
   *  declaration in useProjectChatScroll (I6). */
  contentRef: RefObject<HTMLDivElement>;
  isFollowingRef: RefObject<boolean>;
  /** Guards this hook's own content-growth ResizeObserver effect only —
   *  don't fight an active snap. The tail-signal-driven choice between
   *  snap and follow is the caller's job now (useProjectChatScroll); this
   *  hook only knows how to scroll to the tail, not who else might want
   *  to move the scroller on a given pass. */
  snapPendingRef: RefObject<boolean>;
  scrollProgrammatically: (run: () => void) => void;
}

export const useFollowTail = ({
  scrollRef,
  scrollContentRef,
  followTailRef,
  contentRef,
  isFollowingRef,
  snapPendingRef,
  scrollProgrammatically,
}: UseFollowTailArgs) => {
  const scrollToRenderedTail = useCallback(
    (scroller: HTMLElement) => {
      const tail = followTailRef.current;

      if (tail) {
        const scrollerRect = scroller.getBoundingClientRect();
        const currentTop = scroller.scrollTop;
        const tailBottom =
          currentTop + tail.getBoundingClientRect().bottom - scrollerRect.top;
        const tailTargetTop = tailBottom - scroller.clientHeight;

        // Follow only ever scrolls DOWN to a tail that has dropped below the
        // fold; clamping the target to the current position means it can never
        // scroll UP. Re-pinning upward is what yanked the view on every CUA
        // step, when an approval appeared, and when the thinking indicator was
        // removed at settle (AGE-633) — whenever the tail sits at or above the
        // current position the reader is left exactly where they are. The
        // one-time send-snap is the only thing that scrolls the transcript up.
        scroller.scrollTo({
          top: Math.max(currentTop, tailTargetTop),
        });
        return;
      }

      const content = contentRef.current;
      if (content) {
        content.scrollIntoView({ block: 'end' });
      } else {
        scroller.scrollTo({ top: scroller.scrollHeight });
      }
    },
    [contentRef, followTailRef]
  );

  // Robust follow-tail: any growth of the rendered content (streaming text,
  // a draft writing into a tool call, a sub-agent's child stream, an expanding
  // card, a late-loading image) keeps the tail in view while the user is
  // following. This catches the cases the content-hash effect below cannot,
  // and is a no-op when the user has scrolled away or a send-snap is pending.
  useEffect(() => {
    const scroller = scrollRef.current;
    const content = scrollContentRef.current;
    if (!scroller || !content) return;
    if (typeof ResizeObserver === 'undefined') return;

    const observer = createResizeObserverWithRAF(() => {
      if (snapPendingRef.current) return;
      if (!isFollowingRef.current) return;
      scrollProgrammatically(() => scrollToRenderedTail(scroller));
    });
    observer.observe(content);
    return () => observer.disconnect();
    // Refs are stable object identities for the life of the component (they
    // originate from useRef in useProjectChatScroll and are passed down
    // unchanged); listing them satisfies the lint rule without changing when
    // this effect actually re-runs.
  }, [
    scrollProgrammatically,
    scrollToRenderedTail,
    isFollowingRef,
    scrollContentRef,
    scrollRef,
    snapPendingRef,
  ]);

  return { scrollToRenderedTail };
};
